/**
 * One occasion across a school-pack date and a stored Termin.
 * Storage stays as it is. Callers hide the pack twin when a stored row
 * already covers that child and that occasion.
 * The API uses the same rules in server/lib/regionalCalendar/occasionMatch.js.
 */

const STOP = new Set([
  "uhr",
  "klasse",
  "klassen",
  "und",
  "der",
  "die",
  "das",
  "den",
  "dem",
  "des",
  "fuer",
  "mit",
  "von",
  "vom",
  "zum",
  "zur",
  "ein",
  "eine",
  "einer",
  "eines",
  "am",
  "im",
  "auf",
  "bei",
  "nach",
  "vor",
  "bis",
]);

function fold(raw) {
  return String(raw || "")
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

/**
 * @param {string} startA
 * @param {string} endA
 * @param {string} startB
 * @param {string} endB
 */
export function datesOverlap(startA, endA, startB, endB) {
  if (!startA || !startB) return false;
  const aEnd = endA || startA;
  const bEnd = endB || startB;
  return startA <= bEnd && startB <= aEnd;
}

/**
 * Distinctive tokens: words, a short prefix glued to the next word (Q-Tag),
 * and 1–2 digit class numbers. Years are left out.
 * @param {unknown} text
 * @returns {string[]}
 */
export function occasionTokens(text) {
  const parts = fold(text)
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
  /** @type {string[]} */
  const out = [];
  for (let i = 0; i < parts.length; i += 1) {
    const word = parts[i];
    if (/^\d+$/.test(word)) {
      if (word.length <= 2) out.push(word);
      continue;
    }
    const next = parts[i + 1];
    if (word.length <= 2 && next && !/^\d+$/.test(next) && next.length >= 3) {
      const glued = `${word}${next}`;
      if (!STOP.has(glued) && glued.length >= 4) out.push(glued);
      i += 1;
      continue;
    }
    if (word.length >= 4 && !STOP.has(word)) out.push(word);
  }
  return out;
}

/**
 * @param {{ title?: string, name?: string, match_key?: string }|null|undefined} item
 */
export function tokensForRecord(item) {
  const bits = [item?.match_key, item?.title, item?.name];
  return [...new Set(bits.flatMap((part) => occasionTokens(part)))];
}

function wordsOf(tokens) {
  return tokens.filter((token) => !/^\d+$/.test(token));
}

function numbersOf(tokens) {
  return tokens.filter((token) => /^\d+$/.test(token));
}

function wordHit(left, right) {
  return left.some((a) =>
    right.some((b) => {
      if (a === b) return true;
      if (a.length < 6 || b.length < 6) return false;
      return a.endsWith(b) || b.endsWith(a);
    }),
  );
}

/**
 * Same occasion. A shared class number is required when both names carry one,
 * so Elternabend 4. Klassen does not swallow Elternabend 5. Klassen.
 * @param {{ title?: string, name?: string, match_key?: string }} item
 * @param {{ title?: string, name?: string }} event
 */
export function sameOccasion(item, event) {
  const left = tokensForRecord(item);
  const right = tokensForRecord(event);
  if (!wordHit(wordsOf(left), wordsOf(right))) return false;
  const leftNums = numbersOf(left);
  const rightNums = numbersOf(right);
  if (leftNums.length && rightNums.length) {
    return leftNums.some((n) => rightNums.includes(n));
  }
  return true;
}

function childMatches(item, event) {
  const eventChild = fold(event?.childName || event?.child || "");
  const eventId = String(event?.childId || "");
  if (!eventChild && !eventId) return true;
  const itemChild = fold(item?.child || item?.childName || "");
  const itemId = String(item?.childId || "");
  if (!itemChild && !itemId) return true;
  if (itemId && eventId && itemId === eventId) return true;
  return Boolean(itemChild) && itemChild === eventChild;
}

function covers(item, event) {
  const itemEnd = item?.endDate || item?.endsOn || item?.date;
  return (
    datesOverlap(item?.date || item?.startsOn, itemEnd, event?.startsOn || event?.date, event?.endsOn || event?.endDate || event?.date) &&
    childMatches(item, event) &&
    sameOccasion(item, event)
  );
}

/**
 * Pack rows that still need their own line. A stored Termin hides the pack
 * twin only when it matches that one row. Two occasions on the same day stay.
 * @param {object[]} packDates
 * @param {object[]} items stored mail, flyer, or Termin rows
 */
export function visiblePackDates(packDates, items) {
  const list = Array.isArray(packDates) ? packDates : [];
  const saved = Array.isArray(items) ? items : [];
  return list.filter((pack) => {
    const hits = saved.filter((item) => covers(item, pack));
    if (hits.length === 0) return true;
    const decisive = hits.some(
      (item) => list.filter((other) => covers(item, other)).length === 1,
    );
    return !decisive;
  });
}

/**
 * A stored row that repeats a Ferien or Feiertag line already on screen.
 * @param {object} item
 * @param {object[]} events
 */
export function itemShownOnCalendar(item, events) {
  return (events || []).some(
    (event) =>
      datesOverlap(item?.date, item?.endDate || item?.date, event?.startsOn, event?.endsOn) &&
      sameOccasion(item, event),
  );
}
